import { useEffect, useState } from "react";
import { Header } from "../components/Header";
import { KpiCard } from "../components/KpiCard";
import { AIInsightBanner } from "../components/AIInsightBanner";
import { PageTimeline } from "../components/PageTimeline";
import { usePeriod } from "../lib/PeriodContext";
import api from "../lib/api";
import { fmtINR, fmtNum } from "../lib/format";
import {
  BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend,
} from "recharts";
import { ResponsiveContainer } from "../components/AutoSizer";

const palette = ["#0F1D32", "#D4AF37", "#475569", "#3730A3", "#991B1B", "#065F46"];

export default function ExecutiveCommandCenter() {
  const { asOf, period, loading: periodLoading, projectsQS } = usePeriod();
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    if (!asOf) return;
    api.get(`/kpi/executive?as_of=${asOf}&period=${period}${projectsQS}`).then((r) => setData(r.data));
    api.get(`/kpi/summary-tables?as_of=${asOf}&period=${period}${projectsQS}`).then((r) => setSummary(r.data));
  }, [asOf, period, projectsQS]);

  if (periodLoading || !data) {
    return (
      <>
        <Header title="Executive Command Center" subtitle="Decision Intelligence" />
        <div className="p-6 text-sm text-slate-500">Loading intelligence layer...</div>
      </>
    );
  }

  const k = data.kpis;
  const d = data.delta || {};

  return (
    <>
      <Header title="Executive Command Center" subtitle="Decision Intelligence" />
      <div className="p-3 sm:p-6 md:p-8 max-w-[1600px] mx-auto space-y-5 min-w-0">
        <PageTimeline fyLabel={data.fy_label} />
        <AIInsightBanner
          context={{ period: data.period, fy_label: data.fy_label, as_of: data.as_of, kpis: k, top_projects: data.project_sales?.slice(0, 5) }}
        />

        {/* Sales Row */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-mono mb-2">
            Sales · {data.fy_label} · {period}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard label="Sales Target" value={fmtINR(k.sales_target, { short: true })} testId="kpi-sales-target" />
            <KpiCard label="Gross Sales" value={fmtINR(k.gross_sales, { short: true })} delta={d.gross_sales} testId="kpi-gross-sales" />
            <KpiCard label="Cancellations" value={fmtINR(k.cancel_value, { short: true })} delta={d.cancel_value} testId="kpi-cancellations" />
            <KpiCard label="Net Sales" value={fmtINR(k.net_sales, { short: true })} delta={d.net_sales} accent testId="kpi-net-sales" />
          </div>
        </div>

        {/* Collection Row */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-mono mb-2">
            Collection
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <KpiCard label="Collection Target" value={fmtINR(k.collection_target, { short: true })} testId="kpi-coll-target" />
            <KpiCard label="Actual Collection" value={fmtINR(k.collection, { short: true })} delta={d.collection} testId="kpi-collection" />
            <KpiCard label="Efficiency" value={`${k.collection_efficiency}%`} accent testId="kpi-coll-eff" />
          </div>
        </div>

        {/* Count Row */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-mono mb-2">
            Volume
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <KpiCard label="Sold Units" value={fmtNum(k.sales_count)} dark testId="kpi-sold-units" />
            <KpiCard label="Cancelled" value={fmtNum(k.cancel_count)} dark testId="kpi-cancelled-units" />
            <KpiCard label="Net SBA (sq.ft)" value={fmtNum(k.net_sba)} dark testId="kpi-net-sba" />
          </div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white rounded-md border border-slate-200 p-4">
            <div className="text-sm font-semibold mb-3">Revenue Trend · {data.fy_label}</div>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.monthly_trend}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                <YAxis tickFormatter={(v) => `${(v / 1e7).toFixed(0)}Cr`} tick={{ fontSize: 10 }} />
                <Tooltip formatter={(v) => fmtINR(v, { short: true })} />
                <Bar dataKey="net_sales" fill="#0F1D32" radius={[4, 4, 0, 0]} name="Net Sales" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white rounded-md border border-slate-200 p-4">
            <div className="text-sm font-semibold mb-3">Collection Trend · {data.fy_label}</div>
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={data.monthly_trend}>
                <defs>
                  <linearGradient id="collGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.6} />
                    <stop offset="100%" stopColor="#D4AF37" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                <YAxis tickFormatter={(v) => `${(v / 1e7).toFixed(0)}Cr`} tick={{ fontSize: 10 }} />
                <Tooltip formatter={(v) => fmtINR(v, { short: true })} />
                <Area dataKey="collection" stroke="#D4AF37" fill="url(#collGrad)" strokeWidth={2} name="Collection" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Summary tables */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white rounded-md border border-slate-200">
            <div className="px-4 py-3 border-b border-slate-200 text-sm font-semibold flex items-center justify-between">
              <span>Sales by Project</span>
              <span className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Top 8</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="sales-by-project-table">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Project</th>
                    <th className="text-right px-4 py-2 font-medium">Gross</th>
                    <th className="text-right px-4 py-2 font-medium">Cancel</th>
                    <th className="text-right px-4 py-2 font-medium">Net</th>
                  </tr>
                </thead>
                <tbody>
                  {(summary?.sales_by_project || []).slice(0, 8).map((r) => (
                    <tr key={r.project_name} className="border-t border-slate-100">
                      <td className="px-4 py-2 truncate max-w-[180px]">{r.project_name}</td>
                      <td className="numeric-cell px-4 py-2">{fmtINR(r.gross, { short: true })}</td>
                      <td className="numeric-cell px-4 py-2 text-red-600">{fmtINR(r.cancel, { short: true })}</td>
                      <td className="numeric-cell px-4 py-2 font-semibold">{fmtINR(r.net, { short: true })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-md border border-slate-200">
            <div className="px-4 py-3 border-b border-slate-200 text-sm font-semibold flex items-center justify-between">
              <span>Collection by Project</span>
              <span className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Top 8</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="collection-by-project-table">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Project</th>
                    <th className="text-right px-4 py-2 font-medium">Collection</th>
                  </tr>
                </thead>
                <tbody>
                  {(summary?.collection_by_project || []).slice(0, 8).map((r) => (
                    <tr key={r.project_name} className="border-t border-slate-100">
                      <td className="px-4 py-2 truncate max-w-[180px]">{r.project_name}</td>
                      <td className="numeric-cell px-4 py-2 font-semibold">{fmtINR(r.collection, { short: true })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
